import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Plus, Search, ArrowUpDown, Inbox as InboxIcon, Activity, FileText } from 'lucide-react';
import { PageHeader } from '@/components/layout/AppShell';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, Field } from '@/components/ui/Input';
import { ErrorState } from '@/components/ui/States';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { toast } from '@/components/ui/Toast';
import { runStateTone } from '@/components/runs/RunTranscript';
import { CompanyLogo } from '@/components/CompanyLogo';
import { StatusPill } from '@/components/ui/StatusPill';
import { cn } from '@/lib/cn';
import { useIsStaff } from '@/app/store';
import { useAsync } from '@/lib/useAsync';
import { timeAgo } from '@/lib/timeAgo';
import { api, ApiError, type Overview, type OverviewCompany, type OverviewRun } from '@/lib/api';

const projectPath = (p: OverviewRun['project']) =>
  `/c/${p.companySlug}/w/${p.workspaceSlug}/p/${p.slug}`;

/**
 * Ops-first home: a thin stat strip, then the client book as a dense,
 * filterable, sortable table (the primary object — scales to hundreds of
 * rows), then the activity feeds. No card grids.
 */
export function HomePage() {
  const isStaff = useIsStaff();
  const overview = useAsync(() => api.getOverview(), []);
  const [creating, setCreating] = useState(false);

  // Clients land on their portal; the console is the operator surface.
  if (!isStaff) {
    return <Navigate to="/portal" replace />;
  }

  return (
    <>
      <PageHeader
        kicker="Swarm console"
        title="Overview"
        action={
          isStaff ? (
            <Button onClick={() => setCreating((v) => !v)}>
              <Plus className="h-4 w-4" /> New company
            </Button>
          ) : undefined
        }
      />

      {creating && <NewCompanyForm onDone={() => { setCreating(false); overview.reload(); }} />}

      {overview.status === 'loading' && <SkeletonRows rows={5} />}
      {overview.status === 'error' && <ErrorState error={overview.error} onRetry={overview.reload} />}
      {overview.status === 'success' && (
        <>
          <StatStrip data={overview.data} />
          <CompanyTable companies={overview.data.companies} onChanged={overview.reload} />
          <ActivityRow data={overview.data} />
        </>
      )}
    </>
  );
}

function StatStrip({ data }: { data: Overview }) {
  const stats: { label: string; value: number; to?: string; tone?: 'accent' }[] = [
    { label: 'Needs review', value: data.pendingSuggestionCount, to: '/review', tone: 'accent' },
    { label: 'Running now', value: data.inFlightRuns.length },
    { label: 'Companies', value: data.companies.length },
    { label: 'Recent deliverables', value: data.recentDeliverables.length },
  ];
  return (
    <div className="mb-6 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-4">
      {stats.map((s) => {
        const inner = (
          <>
            <span
              className={`font-display text-[22px] font-semibold leading-none ${
                s.tone === 'accent' && s.value > 0 ? 'text-accent' : 'text-heading'
              }`}
            >
              {s.value}
            </span>
            <span className="mt-1 text-xs text-fg-muted">{s.label}</span>
          </>
        );
        return s.to ? (
          <Link key={s.label} to={s.to} className="flex flex-col bg-surface px-4 py-3.5 transition hover:bg-surface-2/60">
            {inner}
          </Link>
        ) : (
          <div key={s.label} className="flex flex-col bg-surface px-4 py-3.5">
            {inner}
          </div>
        );
      })}
    </div>
  );
}

type SortKey = 'name' | 'activity' | 'pending';
type StatusFilter = 'active' | 'paused' | 'archived' | 'all';

function CompanyTable({ companies, onChanged }: { companies: OverviewCompany[]; onChanged: () => void }) {
  const isStaff = useIsStaff();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('activity');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [busyId, setBusyId] = useState<string | null>(null);

  const statusCount = (s: string) => companies.filter((c) => c.status === s).length;

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const byStatus = statusFilter === 'all' ? companies : companies.filter((c) => c.status === statusFilter);
    const filtered = q
      ? byStatus.filter(
          (c) =>
            c.name.toLowerCase().includes(q) ||
            c.workspaces.some(
              (w) => w.name.toLowerCase().includes(q) || w.projects.some((p) => p.name.toLowerCase().includes(q)),
            ),
        )
      : byStatus;
    return [...filtered].sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'pending') return b.pendingSuggestions - a.pendingSuggestions || a.name.localeCompare(b.name);
      return (b.lastActivityAt ?? '').localeCompare(a.lastActivityAt ?? '') || a.name.localeCompare(b.name);
    });
  }, [companies, query, sort, statusFilter]);

  async function setStatus(companyId: string, status: string) {
    setBusyId(companyId);
    try {
      await api.updateCompany(companyId, { status: status as 'active' | 'paused' | 'archived' });
      onChanged();
    } catch (err) {
      toast.danger('Could not change status', err instanceof ApiError ? err.message : undefined);
    } finally {
      setBusyId(null);
    }
  }

  const header = (label: string, key: SortKey, className = '') => (
    <button
      onClick={() => setSort(key)}
      className={`flex items-center gap-1 text-left text-xs font-medium transition ${
        sort === key ? 'text-heading' : 'text-fg-subtle hover:text-fg-muted'
      } ${className}`}
    >
      {label} <ArrowUpDown className="h-3 w-3 opacity-60" />
    </button>
  );

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-2.5">
        <p className="font-semibold text-fg">Companies</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {(
            [
              ['active', 'Active'],
              ['paused', 'Paused'],
              ['archived', 'Archived'],
            ] as const
          )
            .filter(([key]) => key === 'active' || statusCount(key) > 0)
            .map(([key, label]) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs font-medium transition',
                  statusFilter === key ? 'border-primary bg-primary-muted text-primary' : 'border-border text-fg-muted hover:text-fg',
                )}
              >
                {label} <span className="tabular-nums opacity-70">{statusCount(key)}</span>
              </button>
            ))}
          <button
            onClick={() => setStatusFilter('all')}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition',
              statusFilter === 'all' ? 'border-primary bg-primary-muted text-primary' : 'border-border text-fg-muted hover:text-fg',
            )}
          >
            All
          </button>
        </div>
        <div className="relative ml-auto w-64 max-w-full">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter companies, engagements, projects…"
            className="h-8 w-full rounded-md border border-border bg-bg pl-8 pr-2 text-[13px] focus:border-primary/50 focus:outline-none"
          />
        </div>
      </div>

      <div className="hidden grid-cols-[minmax(180px,1.2fr)_2fr_96px_100px_120px] gap-4 border-b border-border px-4 py-2 md:grid">
        {header('Company', 'name')}
        <span className="text-xs font-medium text-fg-subtle">Engagements</span>
        <span className="text-xs font-medium text-fg-subtle">Status</span>
        {header('Pending', 'pending', 'justify-self-end')}
        {header('Activity', 'activity', 'justify-self-end')}
      </div>

      <ul className="divide-y divide-border">
        {rows.map((c) => (
          <li key={c.id}>
            <div className="grid grid-cols-1 items-center gap-2 px-4 py-2.5 transition hover:bg-surface-2/50 md:grid-cols-[minmax(180px,1.2fr)_2fr_96px_100px_120px] md:gap-4">
              <Link to={`/c/${c.slug}`} className="flex min-w-0 items-center gap-2.5">
                <CompanyLogo name={c.name} websiteUrl={c.websiteUrl} logoUrl={c.logoUrl} size={26} />
                <span className="truncate font-medium text-fg">{c.name}</span>
              </Link>
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                {c.workspaces.slice(0, 3).map((w) => (
                  <Link
                    key={w.id}
                    to={`/c/${c.slug}/w/${w.slug}`}
                    className="truncate rounded-sm border border-border bg-bg px-1.5 py-0.5 text-xs text-fg-muted transition hover:border-primary/40 hover:text-primary"
                  >
                    {w.name}
                  </Link>
                ))}
                {c.workspaces.length > 3 && (
                  <Link to={`/c/${c.slug}`} className="text-xs text-fg-subtle hover:text-primary">
                    +{c.workspaces.length - 3}
                  </Link>
                )}
                {c.workspaces.length === 0 && <span />}
              </div>
              <span onClick={(e) => e.stopPropagation()}>
                <StatusPill
                  status={c.status}
                  busy={busyId === c.id}
                  onChange={isStaff ? (s) => void setStatus(c.id, s) : undefined}
                />
              </span>
              <div className="hidden justify-self-end md:block">
                {c.pendingSuggestions > 0 ? (
                  <Link to="/review" className="rounded-sm bg-accent px-1.5 py-0.5 text-2xs font-semibold text-white">
                    {c.pendingSuggestions}
                  </Link>
                ) : (
                  <span />
                )}
              </div>
              <span className="hidden justify-self-end text-xs tabular-nums text-fg-muted md:block">
                {c.lastActivityAt ? timeAgo(c.lastActivityAt) : ''}
              </span>
            </div>
          </li>
        ))}
      </ul>
      {rows.length === 0 && (
        <p className="px-4 py-6 text-center text-sm text-fg-muted">
          {query.trim()
            ? `No matches for “${query}”.`
            : statusFilter !== 'all' && companies.length > 0
              ? `No ${statusFilter} companies.`
              : 'No companies yet. Create one to get started.'}
        </p>
      )}
    </div>
  );
}

function ActivityRow({ data }: { data: Overview }) {
  const runs = [...data.inFlightRuns, ...data.recentRuns].slice(0, 6);
  return (
    <div className="mt-6 grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader title="Activity" subtitle={runs.length ? undefined : 'No runs yet.'} />
        {runs.length > 0 && (
          <ul className="divide-y divide-border">
            {runs.map((r) => (
              <li key={r.id}>
                <Link
                  to={`${projectPath(r.project)}/runs/${r.id}`}
                  className="flex items-center gap-3 px-5 py-2.5 transition hover:bg-surface-2/60"
                >
                  <Activity className="h-4 w-4 shrink-0 text-fg-subtle" />
                  <span className="min-w-0 flex-1">
                    <p className="truncate text-sm text-fg">
                      <span className="font-medium">{r.agentName ?? r.title}</span>
                      <span className="text-fg-muted"> · {r.project.companyName} · {r.project.name}</span>
                    </p>
                    {r.reason && <p className="truncate text-xs text-fg-subtle">{r.reason}</p>}
                  </span>
                  <span className="text-xs tabular-nums text-fg-subtle">{timeAgo(r.finishedAt ?? r.createdAt)}</span>
                  <Badge tone={runStateTone(r.state)}>{r.state.toLowerCase()}</Badge>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Recently shipped"
          subtitle={data.recentDeliverables.length ? undefined : 'No deliverables yet.'}
          action={
            data.pendingSuggestionCount > 0 ? (
              <Link to="/review" className="flex items-center gap-1.5 text-sm font-medium text-accent hover:underline">
                <InboxIcon className="h-4 w-4" /> {data.pendingSuggestionCount} to review
              </Link>
            ) : undefined
          }
        />
        {data.recentDeliverables.length > 0 && (
          <ul className="divide-y divide-border">
            {data.recentDeliverables.slice(0, 6).map((d) => (
              <li key={d.id}>
                <Link
                  to={`${projectPath(d.project)}/deliverables/${d.id}`}
                  className="flex items-center gap-2.5 px-5 py-2.5 transition hover:bg-surface-2/60"
                >
                  <FileText className="h-4 w-4 shrink-0 text-fg-subtle" />
                  <span className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-fg">{d.title}</p>
                    <p className="truncate text-xs text-fg-subtle">
                      {d.project.companyName} · v{d.version}
                    </p>
                  </span>
                  <span className="text-xs tabular-nums text-fg-subtle">{timeAgo(d.updatedAt)}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function NewCompanyForm({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const company = await api.createCompany(name.trim());
      toast.success('Company created');
      onDone();
      navigate(`/c/${company.slug}`);
    } catch (err) {
      toast.danger('Could not create company', err instanceof ApiError ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mb-6">
      <CardBody>
        <form className="flex flex-wrap items-end gap-3" onSubmit={submit}>
          <div className="min-w-64 flex-1">
            <Field label="Company name" htmlFor="company-name">
              <Input
                id="company-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Betfred"
                required
                autoFocus
              />
            </Field>
          </div>
          <Button type="submit" disabled={busy || !name.trim()}>
            {busy ? 'Creating…' : 'Create'}
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}
